/**
 * Device Session — token durável do run anónimo
 *
 * O token JWT do run (anónimo ou já com email) é o único credential
 * same-device. Para sobreviver a limpezas do localStorage e a mudanças de
 * separador, escrevemo-lo num **cookie** (`selna_device_token`, ~1 ano) e
 * espelhamos em localStorage (resiliência extra).
 *
 * O `selna_game_session` (sessionPersistence.ts) mantém-se para o snapshot
 * de display (título, episódio, etc.) — o cookie é a fonte durável do token.
 *
 * Cross-device não usa cookie: usa magic link (posse da inbox).
 */

const COOKIE_NAME = 'selna_device_token';
const LOCAL_STORAGE_KEY = 'selna_device_token';
// ~1 ano em segundos (365 dias). Coincide com o expiry do token anónimo (B8).
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/**
 * Grava o token num cookie (SameSite=Lax, path=/, ~1 ano) e espelha em
 * localStorage. O cookie é a fonte durável — sobrevive a limpezas do
 * localStorage.
 */
export function saveDeviceToken(token: string): void {
  if (!token || typeof token !== 'string') {
    throw new Error('saveDeviceToken: token is REQUIRED');
  }
  try {
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(token)}; max-age=${MaxAgeSeconds()}; path=/; SameSite=Lax`;
  } catch (err) {
    // Ambientes sem document.cookie (SSR) — ignora silenciosamente.
    console.warn('[DeviceSession] Failed to set cookie:', err);
  }
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, token);
  } catch (err) {
    // localStorage pode estar cheio ou desactivado — o cookie ainda está lá.
    console.warn('[DeviceSession] Failed to mirror token to localStorage:', err);
  }
}

/**
 * Lê o token do device. Prefere o cookie (fonte durável); se não houver,
 * cai para o espelho de localStorage.
 */
export function loadDeviceToken(): string | null {
  // 1. Cookie
  try {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${COOKIE_NAME}=`));
    if (match) {
      const value = match.split('=').slice(1).join('=');
      if (value) return decodeURIComponent(value);
    }
  } catch (err) {
    // ignore
  }
  // 2. Espelho localStorage
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY);
  } catch (err) {
    return null;
  }
}

/**
 * Limpa o token do device (cookie + localStorage). Usado quando o jogador
 * abandona explicitamente o run ou quando um switch (B4 keep:'existing')
 * substitui o token por um novo.
 */
export function clearDeviceToken(): void {
  try {
    // Apagar cookie: max-age=0 expira-o imediatamente.
    document.cookie = `${COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax`;
  } catch (err) {
    // ignore
  }
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch (err) {
    // ignore
  }
}

// Helper isolado para facilitar testes/mocks do max-age.
function MaxAgeSeconds(): number {
  return MAX_AGE_SECONDS;
}
